import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
   Box,
   Typography,
   Button,
   Grid,
   Paper,
   Card,
   CardContent,
   CardMedia,
   CardActions,
   CircularProgress,
   Alert,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogContentText,
   DialogActions,
   Divider,
   TextField,
   InputAdornment,
   Tab,
   Tabs,
   Chip,
   Container,
   Menu,
   MenuItem,
   IconButton,
   Tooltip,
   useMediaQuery,
   useTheme,
} from '@mui/material';
import {
   TravelExplore as TravelExploreIcon,
   Add as AddIcon,
   Search as SearchIcon,
   FilterList as FilterListIcon,
   Clear as ClearIcon,
   CalendarToday as CalendarTodayIcon,
   Edit as EditIcon,
   Delete as DeleteIcon,
   MoreVert as MoreVertIcon,
   People as PeopleIcon,
   Public as PublicIcon,
   Description as DescriptionIcon,
   AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { db } from '../firebase/config';
import { collection, getDocs, orderBy, query, where, Timestamp } from 'firebase/firestore';
import { useUser } from '../contexts/UserContext';
import CreateTripForm from '../components/TripForms/CreateTripForm';
import HistoricalTripForm from '../components/TripForms/HistoricalTripForm';
import TripEventModal, { TripEventModalData } from '../components/modals/TripEventModal';
import { get_all_trips_and_shared_trips } from '../services';
import { useSubscription } from '../contexts/SubscriptionContext';
import CollapsibleInfoPanel from '../components/common/CollapsibleInfoPanel';
import { format } from 'date-fns';
import { useResponsive, useResponsiveTypography, useResponsiveSpacing } from '../utils/responsive';
import { getDefaultBackgroundStyle } from '../utils/defaultImages';

interface Trip {
   id: string;
   title: string;
   description?: string;
   startDate: Date;
   endDate: Date;
   primaryDestination?: {
      name: string;
      coordinates?: [number, number];
   };
   isHistorical?: boolean;
   isPublic?: boolean;
   owner: string;
   coverPhotoUrl?: string;
   coverImageUrl?: string;
   hasCoverPhoto?: boolean;
   isOwner?: boolean;
   isShared?: boolean;
   userRole?: 'owner' | 'editor' | 'viewer';
   createdAt?: Date;
   updatedAt?: Date;
   members?: any[];
   participants?: any[];
}

const TripsPage: React.FC = () => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
   const { isMobile: responsiveIsMobile, deviceType } = useResponsive();
   const { fontSize, getFontSize } = useResponsiveTypography();
   const { spacing, getSpacing } = useResponsiveSpacing();

   const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
   const [historicalFormOpen, setHistoricalFormOpen] = useState(false);
   const [trips, setTrips] = useState<Trip[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
   const [deleteError, setDeleteError] = useState<string | null>(null);
   const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
   const [tripToDelete, setTripToDelete] = useState<string | null>(null);

   // Modal state
   const [modalData, setModalData] = useState<TripEventModalData | null>(null);
   const [modalOpen, setModalOpen] = useState(false);
   const { currentUser } = useUser();
   const navigate = useNavigate();
   const {
      isCurrentSubscription,
      checkSubscriptionStatus,
      setSubscriptionModalOpen,
      currentSubscription,
      hasSubscriptions,
   } = useSubscription();

   // Helper function to check if user has created any trips
   const hasCreatedAnyTrips = async (): Promise<boolean> => {
      if (!currentUser?.uid) return false;
      try {
         // Query all trips and filter by members array where user is owner
         const tripsQuery = query(collection(db, 'trips'));
         const tripsSnapshot = await getDocs(tripsQuery);

         // Filter trips where user is owner in members array
         const userOwnedTrips = tripsSnapshot.docs.filter((doc) => {
            const tripData = doc.data();
            const members = tripData.members || [];
            const userMember = members.find((m: any) => m.userId === currentUser.uid);
            return userMember && userMember.role === 'owner';
         });

         return userOwnedTrips.length > 0;
      } catch (error) {
         console.error('Error checking trips creation status:', error);
         return false;
      }
   };

   // Helper function to check if user can create trip
   const canCreateTrip = async (): Promise<boolean> => {
      const hasCreatedTrips = await hasCreatedAnyTrips();

      // Check for paid subscription only (not invited access)
      const hasPaidSubscription =
         hasSubscriptions &&
         currentSubscription &&
         (currentSubscription.status === 'active' ||
            currentSubscription.status === 'trialing' ||
            currentSubscription.status === 'past_due');

      // If user hasn't created any trips yet, always allow
      if (!hasCreatedTrips) {
         return true;
      }

      // If user has created trips, only allow if they have PAID subscription
      return hasPaidSubscription;
   };

   // Fetch trips from backend API
   const fetchTrips = async () => {
      if (!currentUser) {
         console.log('No current user, skipping fetch');
         return;
      }

      console.log('Fetching trips for user:', currentUser.uid, currentUser.email);
      setLoading(true);
      setError(null);

      try {
         const { getAuthHeaders } = await import('../utils/auth');
         const headers = await getAuthHeaders();

         const apiUrl = `${process.env.REACT_APP_API_URL}/api/trips/user/${currentUser.uid}?userEmail=${encodeURIComponent(currentUser.email || '')}`;
         console.log('API URL:', apiUrl);

         const response = await fetch(apiUrl, {
            method: 'GET',
            headers,
         });

         console.log('Response status:', response.status);
         console.log('Response ok:', response.ok);

         if (!response.ok) {
            const errorText = await response.text();
            console.error('API Error Response:', errorText);
            throw new Error(`Failed to fetch trips: ${response.status} ${errorText}`);
         }

         const result = await response.json();
         console.log('API Response:', result);

         const tripsData = result.data || [];
         console.log('Trips data:', tripsData);

         const _tripsData: Trip[] = tripsData.map((trip: any) => ({
            id: trip.id,
            title: trip.title || 'Unnamed Trip',
            description: trip.description,
            startDate: trip.startDate ? new Date(trip.startDate) : new Date(),
            endDate: trip.endDate ? new Date(trip.endDate) : new Date(),
            primaryDestination: trip.primaryDestination,
            isHistorical: trip.isHistorical || false,
            isPublic: trip.isPublic || false,
            owner: trip.owner || trip.createdBy,
            coverPhotoUrl: trip.coverPhotoUrl || trip.coverImageUrl || null,
            hasCoverPhoto: trip.hasCoverPhoto || false,
            isOwner: trip.isOwner,
            isShared: trip.isShared,
            userRole: trip.userRole, // Include the role information from backend
            createdAt: trip.createdAt ? new Date(trip.createdAt) : undefined,
            updatedAt: trip.updatedAt ? new Date(trip.updatedAt) : undefined,
            members: trip.members || [],
            participants: trip.participants || [],
         }));

         console.log('Processed trips:', _tripsData);
         setTrips(_tripsData);
      } catch (err) {
         console.error('Error fetching trips:', err);
         setError(`Failed to load trips: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
         setLoading(false);
      }
   };

   // Fetch trips on component mount and when currentUser changes
   useEffect(() => {
      if (currentUser) {
         fetchTrips();
      }
   }, [currentUser]);

   const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
   };

   const handleMenuClose = () => {
      setAnchorEl(null);
   };

   const handleAddHistoricalTrip = () => {
      handleMenuClose();
      setHistoricalFormOpen(true);
   };

   const handleTripCreated = async (tripId: string) => {
      fetchTrips(); // Actually refresh the trips list
   };

   const handleViewTrip = (tripId: string) => {
      navigate(`/trip/${tripId}`);
   };

   // Handle opening trip modal
   const handleTripClick = (trip: any) => {
      const modalData: TripEventModalData = {
         id: trip.id,
         type: 'trip',
         title: trip.title,
         description: trip.description,
         startDate: trip.startDate ? new Date(trip.startDate) : undefined,
         endDate: trip.endDate ? new Date(trip.endDate) : undefined,
         destination: trip.primaryDestination?.name || trip.destination,
         location: trip.primaryDestination?.name || trip.destination,
         coverImageUrl: trip.coverPhotoUrl || trip.coverImageUrl,
         createdByName: trip.createdByName,
         participantCount: trip.participantCount,
         photoCount: trip.photoCount,
      };
      setModalData(modalData);
      setModalOpen(true);
   };

   const handleEditTrip = (tripId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      navigate(`/trip/${tripId}`);
   };

   const handleDeleteTrip = (tripId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      console.log('🚨 Delete trip clicked - opening modal, not window.confirm');
      setTripToDelete(tripId);
      setDeleteDialogOpen(true);
   };

   const handleConfirmDelete = async () => {
      if (!tripToDelete) return;

      setDeleteLoading(tripToDelete);
      setDeleteError(null);
      setDeleteDialogOpen(false);

      try {
         console.log('🗑️ Deleting trip:', tripToDelete, 'for user:', currentUser?.uid);

         const { createApiUrl } = await import('../utils/apiConfig');
         const { getAuthHeaders } = await import('../utils/auth');
         const headers = await getAuthHeaders();
         const apiUrl = createApiUrl(`/api/trips/${tripToDelete}?userId=${currentUser?.uid}`);

         console.log('🌐 API URL:', apiUrl);

         const response = await fetch(apiUrl, {
            method: 'DELETE',
            headers,
         });

         console.log('🗑️ Delete response status:', response.status);

         if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('🗑️ Delete error response:', errorData);
            throw new Error(
               errorData.error || errorData.message || `Failed to delete trip (${response.status})`
            );
         }

         console.log('✅ Trip deleted successfully');
         // Remove trip from local state
         setTrips(trips.filter((trip) => trip.id !== tripToDelete));
      } catch (err) {
         console.error('❌ Error deleting trip:', err);
         setDeleteError(
            err instanceof Error ? err.message : 'Failed to delete trip. Please try again.'
         );
      } finally {
         setDeleteLoading(null);
         setTripToDelete(null);
      }
   };

   const handleCancelDelete = () => {
      setDeleteDialogOpen(false);
      setTripToDelete(null);
   };

   // Render trips grid
   const renderTrips = () => {
      if (loading) {
         return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
               <CircularProgress />
            </Box>
         );
      }

      if (error) {
         return (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
               <Typography color='error'>{error}</Typography>
               <Button variant='outlined' onClick={fetchTrips} sx={{ mt: 2 }}>
                  Try Again
               </Button>
            </Paper>
         );
      }

      if (trips.length === 0) {
         return (
            <Box
               sx={{
                  p: 5,
                  textAlign: 'center',
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  my: 3,
               }}
            >
               <Box
                  sx={{
                     bgcolor: 'primary.light',
                     color: 'primary.contrastText',
                     borderRadius: '50%',
                     width: 80,
                     height: 80,
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     mb: 2,
                  }}
               >
                  <TravelExploreIcon sx={{ fontSize: 40 }} />
               </Box>
               <Typography variant='h5' color='primary' gutterBottom>
                  Start Your Journey
               </Typography>
               <Typography variant='body1' color='text.secondary' paragraph sx={{ maxWidth: 600 }}>
                  You haven't created any trips yet. Document your travel experiences, plan upcoming
                  adventures, and organize all your trip details in one place.
               </Typography>
               <Typography variant='body1' color='text.secondary' paragraph sx={{ maxWidth: 600 }}>
                  With Wetrippen, you can track your past travels, plan new adventures, and share
                  your experiences with friends and family. Our AI-powered tools make trip planning
                  easier than ever!
               </Typography>
               <Button
                  variant='contained'
                  color='primary'
                  startIcon={<AddIcon />}
                  size='large'
                  onClick={async () => {
                     if (await canCreateTrip()) {
                        handleAddHistoricalTrip();
                     } else {
                        setSubscriptionModalOpen(true);
                     }
                  }}
                  sx={{ mt: 2 }}
               >
                  Create Your First Trip
               </Button>
            </Box>
         );
      }

      return (
         <Grid container spacing={isMobile ? 2 : 3}>
            {trips.map((trip) => {
               // Determine user's role in this trip
               const userRole = trip.userRole || (trip.isOwner ? 'owner' : 'viewer');
               const canEdit = userRole === 'owner' || userRole === 'editor';
               const canDelete = userRole === 'owner';

               return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={trip.id}>
                     <Card
                        sx={{
                           maxHeight: isMobile ? 360 : 320,
                           display: 'flex',
                           flexDirection: 'column',
                           transition: 'transform 0.2s',
                           '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: 3,
                           },
                           cursor: 'pointer',
                           position: 'relative',
                           borderRadius: isMobile ? 1 : 2,
                        }}
                        onClick={() => handleTripClick(trip)}
                     >
                        {/* Role badge */}
                        <Chip
                           label={
                              userRole === 'owner'
                                 ? 'Owner'
                                 : userRole === 'editor'
                                   ? 'Editor'
                                   : 'Viewer'
                           }
                           size='small'
                           color={
                              userRole === 'owner'
                                 ? 'primary'
                                 : userRole === 'editor'
                                   ? 'secondary'
                                   : 'default'
                           }
                           sx={{
                              position: 'absolute',
                              top: 8,
                              left: 8,
                              zIndex: 2,
                              bgcolor:
                                 userRole === 'owner'
                                    ? 'primary.main'
                                    : userRole === 'editor'
                                      ? 'secondary.main'
                                      : 'rgba(0, 0, 0, 0.6)',
                              color: 'white',
                              fontSize: '0.7rem',
                           }}
                        />

                        {/* Cover Image */}
                        <Box
                           sx={{
                              height: isMobile ? 180 : 160,
                              overflow: 'hidden',
                              position: 'relative',
                              ...getDefaultBackgroundStyle(
                                 'trip',
                                 trip.id,
                                 trip.coverPhotoUrl || trip.coverImageUrl
                              ),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                           }}
                        >
                           {!(trip.coverPhotoUrl || trip.coverImageUrl) && (
                              <>
                                 <Box
                                    sx={{
                                       position: 'absolute',
                                       top: 0,
                                       left: 0,
                                       right: 0,
                                       bottom: 0,
                                       background: 'rgba(0, 0, 0, 0.3)',
                                       zIndex: 1,
                                    }}
                                 />
                                 <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
                                    <TravelExploreIcon
                                       sx={{ fontSize: 40, color: 'white', mb: 1, opacity: 0.9 }}
                                    />
                                 </Box>
                              </>
                           )}

                           {/* Date chip overlay */}
                           <Chip
                              label={`${format(trip.startDate, 'MMM d')} - ${format(trip.endDate, 'MMM d')}`}
                              size='small'
                              sx={{
                                 position: 'absolute',
                                 bottom: 8,
                                 right: 8,
                                 bgcolor: 'rgba(255, 255, 255, 0.9)',
                                 fontWeight: 'bold',
                                 boxShadow: 1,
                              }}
                           />
                        </Box>

                        <CardContent
                           sx={{
                              flexGrow: 1,
                              py: isMobile ? 1.5 : 1,
                              px: isMobile ? 2 : 1.5,
                           }}
                        >
                           {/* Title and Status - compact */}
                           <Box
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 alignItems: 'flex-start',
                                 mb: isMobile ? 1 : 0.5,
                              }}
                           >
                              <Typography
                                 variant='subtitle1'
                                 component='div'
                                 noWrap
                                 sx={{
                                    fontWeight: 600,
                                    fontSize: isMobile ? '1rem' : '0.95rem',
                                    lineHeight: isMobile ? 1.3 : 1.2,
                                 }}
                              >
                                 {trip.title}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                 {trip.isHistorical && (
                                    <Chip
                                       label='Historical'
                                       size='small'
                                       color='primary'
                                       variant='outlined'
                                       sx={{ fontSize: '0.6rem', height: '18px' }}
                                    />
                                 )}
                                 {trip.isPublic && (
                                    <Chip
                                       label='Public'
                                       size='small'
                                       variant='outlined'
                                       sx={{ fontSize: '0.6rem', height: '18px' }}
                                    />
                                 )}
                              </Box>
                           </Box>

                           {/* Destination - compact format */}
                           {trip.primaryDestination && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                 <TravelExploreIcon
                                    fontSize='small'
                                    sx={{ color: 'text.secondary', mr: 0.5, fontSize: '0.9rem' }}
                                 />
                                 <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    noWrap
                                    sx={{ fontSize: '0.7rem' }}
                                 >
                                    {trip.primaryDestination.name}
                                 </Typography>
                              </Box>
                           )}

                           {/* Description - compact */}
                           {trip.description && (
                              <Typography
                                 variant='body2'
                                 color='text.secondary'
                                 sx={{
                                    fontSize: '0.7rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    mb: 0.5,
                                    lineHeight: 1.2,
                                 }}
                              >
                                 {trip.description}
                              </Typography>
                           )}

                           {/* Compact metadata row */}
                           <Box
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 alignItems: 'center',
                                 mt: 0.5,
                              }}
                           >
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                 <PeopleIcon
                                    fontSize='small'
                                    sx={{ color: 'text.secondary', mr: 0.3, fontSize: '0.9rem' }}
                                 />
                                 <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    sx={{ fontSize: '0.65rem' }}
                                 >
                                    {trip.members?.length || trip.participants?.length || 1} members
                                 </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                 <AccessTimeIcon
                                    fontSize='small'
                                    sx={{ color: 'text.secondary', mr: 0.3, fontSize: '0.9rem' }}
                                 />
                                 <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    sx={{ fontSize: '0.65rem' }}
                                 >
                                    {format(trip.startDate, 'MMM d, yyyy')} -{' '}
                                    {format(trip.endDate, 'MMM d, yyyy')}
                                 </Typography>
                              </Box>
                           </Box>
                        </CardContent>

                        {/* Role-based action buttons */}
                        {(canEdit || canDelete) && (
                           <CardActions
                              sx={{ justifyContent: 'flex-end', pt: 0, pb: 0.5, px: 1.5 }}
                           >
                              {canEdit && (
                                 <Tooltip title='Edit Trip'>
                                    <IconButton
                                       size='small'
                                       color='primary'
                                       onClick={(e) => handleEditTrip(trip.id, e)}
                                    >
                                       <EditIcon />
                                    </IconButton>
                                 </Tooltip>
                              )}
                              {canDelete && (
                                 <Tooltip title='Delete Trip'>
                                    <IconButton
                                       size='small'
                                       color='error'
                                       onClick={(e) => handleDeleteTrip(trip.id, e)}
                                       disabled={deleteLoading === trip.id}
                                    >
                                       {deleteLoading === trip.id ? (
                                          <CircularProgress size={20} />
                                       ) : (
                                          <DeleteIcon />
                                       )}
                                    </IconButton>
                                 </Tooltip>
                              )}
                           </CardActions>
                        )}
                     </Card>
                  </Grid>
               );
            })}
         </Grid>
      );
   };

   return (
      <Container
         maxWidth='lg'
         sx={{
            py: isMobile ? 2 : 4,
            px: isMobile ? 1 : 2,
         }}
      >
         <Box
            sx={{
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: isMobile ? 'flex-start' : 'center',
               mb: isMobile ? 2 : 3,
               flexDirection: isMobile ? 'column' : 'row',
               gap: isMobile ? 2 : 0,
            }}
         >
            <Typography
               variant='h4'
               component='h1'
               gutterBottom
               sx={{
                  fontSize: isMobile ? fontSize.h4 : fontSize.h3,
                  textAlign: isMobile ? 'center' : 'left',
               }}
            >
               My Trips
            </Typography>
            <Box>
               <Button
                  variant='contained'
                  color='primary'
                  startIcon={<AddIcon />}
                  onClick={handleMenuClick}
                  size={isMobile ? 'large' : 'medium'}
                  sx={{
                     minHeight: isMobile ? '48px' : '40px',
                     px: isMobile ? 3 : 2,
                     fontSize: isMobile ? fontSize.body1 : 'inherit',
                     width: isMobile ? '100%' : 'auto',
                     maxWidth: isMobile ? '200px' : 'none',
                  }}
               >
                  Add Trip
               </Button>
               <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                  {/* <MenuItem component={Link} to='/trip-planner' onClick={handleMenuClose}>
                    Plan New Trip
                 </MenuItem> */}
                  <MenuItem
                     onClick={async () => {
                        if (await canCreateTrip()) {
                           handleAddHistoricalTrip();
                        } else {
                           setSubscriptionModalOpen(true);
                        }
                     }}
                  >
                     Add Trip
                  </MenuItem>
               </Menu>
            </Box>
         </Box>

         {/* Delete Error Alert */}
         {deleteError && (
            <Alert severity='error' sx={{ mb: 3 }} onClose={() => setDeleteError(null)}>
               {deleteError}
            </Alert>
         )}

         {/* Stats cards */}
         {!loading && !error && trips.length > 0 && (
            <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: isMobile ? 3 : 4 }}>
               <Grid item xs={6} sm={6} md={3}>
                  <Paper
                     elevation={1}
                     sx={{
                        p: isMobile ? 1.5 : 2,
                        textAlign: 'center',
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        borderRadius: isMobile ? 1 : 2,
                     }}
                  >
                     <Typography
                        variant='h3'
                        gutterBottom
                        sx={{
                           fontSize: isMobile ? fontSize.h4 : fontSize.h2,
                        }}
                     >
                        {trips.length}
                     </Typography>
                     <Typography
                        variant='subtitle1'
                        sx={{
                           fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                        }}
                     >
                        Total trips
                     </Typography>
                  </Paper>
               </Grid>
               <Grid item xs={6} sm={6} md={3}>
                  <Paper
                     elevation={1}
                     sx={{
                        p: isMobile ? 1.5 : 2,
                        textAlign: 'center',
                        bgcolor: 'secondary.light',
                        color: 'secondary.contrastText',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        borderRadius: isMobile ? 1 : 2,
                     }}
                  >
                     <Typography
                        variant='h3'
                        gutterBottom
                        sx={{
                           fontSize: isMobile ? fontSize.h4 : fontSize.h2,
                        }}
                     >
                        {trips.filter((trip) => trip.startDate > new Date()).length}
                     </Typography>
                     <Typography
                        variant='subtitle1'
                        sx={{
                           fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                        }}
                     >
                        Upcoming trips
                     </Typography>
                  </Paper>
               </Grid>
               <Grid item xs={6} sm={6} md={3}>
                  <Paper
                     elevation={1}
                     sx={{
                        p: isMobile ? 1.5 : 2,
                        textAlign: 'center',
                        bgcolor: 'success.light',
                        color: 'success.contrastText',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        borderRadius: isMobile ? 1 : 2,
                     }}
                  >
                     <Typography
                        variant='h3'
                        gutterBottom
                        sx={{
                           fontSize: isMobile ? fontSize.h4 : fontSize.h2,
                        }}
                     >
                        {trips.filter((trip) => trip.isHistorical === true).length}
                     </Typography>
                     <Typography
                        variant='subtitle1'
                        sx={{
                           fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                        }}
                     >
                        Historical trips
                     </Typography>
                  </Paper>
               </Grid>
               <Grid item xs={6} sm={6} md={3}>
                  <Paper
                     elevation={1}
                     sx={{
                        p: isMobile ? 1.5 : 2,
                        textAlign: 'center',
                        bgcolor: 'warning.light',
                        color: 'warning.contrastText',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        borderRadius: isMobile ? 1 : 2,
                     }}
                  >
                     <Typography
                        variant='h3'
                        gutterBottom
                        sx={{
                           fontSize: isMobile ? fontSize.h4 : fontSize.h2,
                        }}
                     >
                        {trips.filter((trip) => trip.hasCoverPhoto === true).length}
                     </Typography>
                     <Typography
                        variant='subtitle1'
                        sx={{
                           fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                        }}
                     >
                        Trips with photos
                     </Typography>
                  </Paper>
               </Grid>
            </Grid>
         )}

         {renderTrips()}

         {/* Historical Trip Form Dialog */}
         <HistoricalTripForm
            open={historicalFormOpen}
            onClose={() => setHistoricalFormOpen(false)}
            onSuccess={handleTripCreated}
         />

         {/* Trip/Event Modal */}
         <TripEventModal
            open={modalOpen}
            onClose={() => {
               setModalOpen(false);
               setModalData(null);
            }}
            data={modalData}
         />

         {/* Delete Confirmation Dialog */}
         <Dialog
            open={deleteDialogOpen}
            onClose={handleCancelDelete}
            aria-labelledby='delete-trip-dialog-title'
            aria-describedby='delete-trip-dialog-description'
            PaperProps={{
               sx: {
                  borderRadius: isMobile ? 3 : 2,
                  mx: isMobile ? 2 : 'auto',
                  width: isMobile ? 'calc(100% - 32px)' : 'auto',
                  maxWidth: isMobile ? '100%' : '444px',
               },
            }}
         >
            <DialogTitle
               id='delete-trip-dialog-title'
               sx={{
                  fontSize: isMobile ? fontSize.h6 : fontSize.h6,
                  fontWeight: 600,
               }}
            >
               Confirm Delete
            </DialogTitle>
            <DialogContent>
               <DialogContentText
                  id='delete-trip-dialog-description'
                  sx={{
                     fontSize: isMobile ? fontSize.body1 : fontSize.body2,
                     color: 'text.secondary',
                  }}
               >
                  Are you sure you want to delete this trip? This action cannot be undone.
               </DialogContentText>
            </DialogContent>
            <DialogActions
               sx={{
                  px: isMobile ? 3 : 2,
                  pb: isMobile ? 3 : 2,
                  gap: isMobile ? 1 : 0.5,
                  flexDirection: isMobile ? 'column' : 'row',
               }}
            >
               <Button
                  onClick={handleCancelDelete}
                  variant='outlined'
                  fullWidth={isMobile}
                  sx={{
                     minHeight: isMobile ? '48px' : '36px',
                     fontSize: isMobile ? fontSize.body1 : fontSize.body2,
                     order: isMobile ? 2 : 1,
                  }}
               >
                  Cancel
               </Button>
               <Button
                  onClick={handleConfirmDelete}
                  color='error'
                  variant='contained'
                  fullWidth={isMobile}
                  autoFocus
                  sx={{
                     minHeight: isMobile ? '48px' : '36px',
                     fontSize: isMobile ? fontSize.body1 : fontSize.body2,
                     order: isMobile ? 1 : 2,
                  }}
               >
                  Delete
               </Button>
            </DialogActions>
         </Dialog>
      </Container>
   );
};

export default TripsPage;